import { useEffect } from 'react';
import { parseMusicVideoDoc } from 'fym-shared/src/music24/musicVideoDoc';
import type { SerializedMusicVideoCut } from 'fym-shared/src/music24/musicVideoApi';
import type { CutAutosave, CutSaveState } from './useCutAutosave';
import type { MusicVideoDocDispatch } from './useMusicVideoDoc';

/** The save states in which a newer cut of the server may replace the open document. */
const LOADABLE: readonly CutSaveState[] = ['saved', 'stale', 'working'];

/**
 * True when a cut of the server must replace the open document: it is newer than the revision the
 * editor knows, and the editor holds no change of its own (or its save was refused as stale, or
 * the director wrote it). Pure.
 */
export function shouldLoadServerCut(serverRevision: number, knownRevision: number, saveState: CutSaveState): boolean {
  return serverRevision > knownRevision && LOADABLE.includes(saveState);
}

interface UseServerCutSyncProps {
  cut: SerializedMusicVideoCut;
  durationSec: number;
  localRevision: number;
  dispatch: MusicVideoDocDispatch;
  autosave: CutAutosave;
  /** False in showcase mode: there is no cut on the server to take in. */
  enabled?: boolean;
}

/**
 * Takes the newest cut of the server into the editor (spec §4.4): the director wrote it (the first
 * cut, a recut), another member saved it, or a save of this editor was stale (409) and the cut was
 * read again. A change of this editor that is not saved yet stays; its save then meets the 409 and
 * the editor loads the newest cut with the note "Another change came in".
 *
 * The effect also runs at each stale save (`autosave.staleCount`): after a 409 the read of the cut
 * can answer the cut that the cache holds already (RTK keeps an equal answer as the same object),
 * so a change of `cut` alone would never come, and the editor would stay stale.
 */
export function useServerCutSync(props: UseServerCutSyncProps): void {
  const { cut, durationSec, localRevision, dispatch, autosave, enabled = true } = props;
  const { saveState, markSaved, serverRevisionRef, staleCount } = autosave;
  useEffect(() => {
    if (!enabled || !shouldLoadServerCut(cut.revision, serverRevisionRef.current, saveState)) {
      return;
    }
    const parsed = parseMusicVideoDoc(cut.doc, { durationSec });
    if (!parsed.ok) {
      return;
    }
    const { doc } = parsed;
    // The load raises the local revision by one: the autosave knows that revision as saved.
    markSaved(localRevision + 1, cut.revision);
    dispatch({ type: 'load', doc });
    // Only a new cut of the server runs this; the local revision is read at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cut, durationSec, enabled, staleCount]);
}
